import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DataTableDirective } from 'angular-datatables';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { OrdenesConvenioService } from 'src/app/services/transacciones/ordenes-convenio.service';
import { LanguageApp } from '../../laboratorio/laboratorios/laboratorios.component';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-ordenes-convenio',
  templateUrl: './ordenes-convenio.component.html',
  styleUrls: ['./ordenes-convenio.component.scss'],
  standalone: false
})
export class OrdenesConvenioComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  isDtInitialized = false;

  public ordenes: any[] = [];
  public sucursales: any[] = [];
  public doctores: any[] = [];
  public spinnerBoolean = false;
  public fechaInicio = '';
  public fechaFin = '';
  public sucursal = '';
  public doctor = '';
  public seleccionadas: Record<string, boolean> = {};

  get token(): string { return localStorage.getItem('token') || ''; }
  get totalSeleccionadas(): number {
    return Object.keys(this.seleccionadas).filter(id => this.seleccionadas[id]).length;
  }

  constructor(
    private ordenesConvenioService: OrdenesConvenioService,
    private router: Router,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.buildDtOptions();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
    this.isDtInitialized = true;
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }

  aplicarFiltros(): void {
    this.seleccionadas = {};
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  crearFactura(): void {
    const ids = Object.keys(this.seleccionadas).filter(id => this.seleccionadas[id]);

    if (!this.doctor) {
      Swal.fire({
        icon: 'info',
        title: 'Seleccione un doctor',
        text: 'Debe seleccionar el doctor del convenio antes de marcar órdenes.'
      });
      return;
    }

    if (!ids.length) {
      Swal.fire({
        icon: 'info',
        title: 'Seleccione al menos una orden',
        text: 'Marque las órdenes que desea agrupar en la factura.'
      });
      return;
    }

    this.router.navigate(['/sistema/crear-factura-convenio', this.makeid(30) + btoa(ids.join(',')) + this.makeid(30), this.doctor]);
  }

  estaSeleccionada(orden: any): boolean {
    return !!this.seleccionadas[orden.COD_ORD];
  }

  cambiarDoctor(): void {
    this.seleccionadas = {};
  }

  cambiarSeleccion(orden: any, event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input?.checked === true;

    if (!this.doctor) {
      if (input) input.checked = false;
      delete this.seleccionadas[orden.COD_ORD];
      Swal.fire({ icon: 'info', title: 'Seleccione un doctor' });
      return;
    }

    if (String(orden.CODN_DOC) !== String(this.doctor)) {
      if (input) input.checked = false;
      delete this.seleccionadas[orden.COD_ORD];
      Swal.fire({
        icon: 'warning',
        title: 'Doctor diferente',
        text: 'El doctor seleccionado es diferente al de la orden.'
      });
      return;
    }

    if (checked) {
      this.seleccionadas[orden.COD_ORD] = true;
    } else {
      delete this.seleccionadas[orden.COD_ORD];
    }
  }

  exportarExcel(): void {
    this.dtElement?.dtInstance?.then(dt => {
      this.spinnerBoolean = true;

      this.ordenesConvenioService.listarOrdenes(this.token, {
        fechaInicio: this.fechaInicio,
        fechaFin: this.fechaFin,
        doctor: this.doctor,
        sucursal: this.sucursal,
        start: 0,
        length: -1,
        search: dt.search() || ''
      }).subscribe({
        next: (resp: any) => {
          const ordenes = Array.isArray(resp?.data) ? resp.data : [];
          this.descargarExcel(ordenes);
          this.spinnerBoolean = false;
        },
        error: () => {
          this.spinnerBoolean = false;
        }
      });
    });
  }

  totalServicios(servicios: any[]): number {
    return (servicios || []).reduce((total, servicio) => total + Number(servicio.valor || 0), 0);
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    try {
      const d = new Date(fecha);
      const dia = d.toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' }).split('-').reverse().join('/');
      const hora = d.toLocaleTimeString('en-GB', { timeZone: 'America/Guayaquil', hour12: false }).slice(0, 5);
      return `${dia} ${hora}`;
    } catch {
      return fecha;
    }
  }

  private cargarCatalogos(): void {
    this.ordenesConvenioService.catalogos(this.token).subscribe({
      next: (resp: any) => {
        this.sucursales = Array.isArray(resp?.sucursales) ? resp.sucursales : [];
        this.doctores = Array.isArray(resp?.doctores) ? resp.doctores : [];
      },
      error: () => {
        this.sucursales = [];
        this.doctores = [];
      }
    });
  }

  private buildDtOptions(): void {
    this.dtOptions = {
      language: LanguageApp.spanish_datatables,
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      processing: true,
      responsive: true,
      orderClasses: false,
      searchDelay: 1500,
      ajax: (dtParams: any, callback: any) => {
        this.ngZone.run(() => { this.ordenes = []; this.spinnerBoolean = true; });

        this.ordenesConvenioService.listarOrdenes(this.token, {
          fechaInicio: this.fechaInicio,
          fechaFin: this.fechaFin,
          doctor: this.doctor,
          sucursal: this.sucursal,
          start: dtParams.start,
          length: dtParams.length,
          search: dtParams.search?.value || ''
        }).subscribe({
          next: (resp: any) => {
            this.ngZone.run(() => {
              this.ordenes = Array.isArray(resp?.data) ? resp.data : [];
              this.spinnerBoolean = false;
              callback({
                recordsTotal: resp?.recordsTotal || 0,
                recordsFiltered: resp?.recordsFiltered || 0,
                data: []
              });
            });
          },
          error: () => {
            this.ngZone.run(() => {
              this.ordenes = [];
              this.spinnerBoolean = false;
              callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
            });
          }
        });
      }
    };
  }

  private descargarExcel(ordenes: any[]): void {
    const rows = ordenes.map((orden, index) => ({
      '#': index + 1,
      'N. Orden': orden.NUM_ORD,
      Paciente: orden.NOM_CLI,
      'Cod. Doctor': orden.CODN_DOC,
      'Nombre Doctor': orden.NOM_DOC,
      Sucursal: orden.DIRSUCS_ORD,
      Responsable: orden.responsable,
      'Fec. registro': this.formatFecha(orden.fecha),
      Servicios: this.serviciosTexto(orden.servicios)
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 6 }, { wch: 18 }, { wch: 30 }, { wch: 12 }, { wch: 30 },
      { wch: 24 }, { wch: 24 }, { wch: 20 }, { wch: 60 }
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ordenes Convenio');
    XLSX.writeFile(workbook, `ordenes-convenio-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  private serviciosTexto(servicios: any[]): string {
    return (servicios || [])
      .map(servicio => `${servicio.producto}: $${Number(servicio.valor || 0).toFixed(2)}`)
      .join(' | ');
  }

  private makeid(length: number): string {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }
}
